import React, { useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Favorite as HeartIcon, HeartBroken as BrokenIcon, WhatsApp as WhatsAppIcon, Home as HomeIcon, MenuBook as BookIcon } from '@mui/icons-material';
import DonateButton from '../../components/DonateButton';

// Pages de retour apres un don : /merci (don valide) et /don-annule (don abandonne).
// Ce sont de simples pages d'accueil : elles ne prouvent pas qu'un don a eu lieu (n'importe qui peut les ouvrir)
// et ne contiennent aucune donnee personnelle ni de paiement. Adresses a renseigner chez le prestataire
// (PayPal : « page de remerciement » / « URL d'annulation » du bouton de don).
const TEXTS = {
  fr: {
    thanksTitle: 'Merci pour votre soutien !',
    thanksBody: "Votre geste aide à garder le site gratuit et à faire vivre la langue duala : l'éphéméride, le dictionnaire, les prononciations, le quiz et leurs nouveautés. Chaque contribution compte, quelle que soit sa taille.",
    thanksNote: "Si vous avez fait un don par Orange Money ou MTN, un message de confirmation vous est envoyé par l'opérateur.",
    share: 'Partager le site', shareText: "Je soutiens l'Éphéméride Duala : un proverbe duala chaque jour, un dictionnaire illustré, un quiz. Découvrez-le :",
    cancelTitle: "Votre don n'a pas été finalisé",
    cancelBody: "Aucun montant n'a été prélevé. Vous avez peut-être interrompu le paiement : ce n'est pas grave, et vous pouvez réessayer quand vous le souhaitez.",
    cancelNote: "Un problème de paiement ? Écrivez-nous, nous vous aiderons volontiers.",
    home: "Retour à l'éphéméride", dictionary: 'Voir le dictionnaire',
  },
  en: {
    thanksTitle: 'Thank you for your support!',
    thanksBody: 'Your gift helps keep the site free and the Duala language alive: the daily calendar, the dictionary, the pronunciations, the quiz and their new features. Every contribution counts, whatever its size.',
    thanksNote: 'If you donated by Orange Money or MTN, your operator sends you a confirmation message.',
    share: 'Share the site', shareText: 'I support the Duala Ephemeris: a Duala proverb every day, an illustrated dictionary, a quiz. Take a look:',
    cancelTitle: 'Your donation was not completed',
    cancelBody: 'No amount has been charged. You may have interrupted the payment: that is fine, and you can try again whenever you like.',
    cancelNote: 'Having trouble paying? Write to us, we will gladly help.',
    home: 'Back to the daily calendar', dictionary: 'See the dictionary',
  },
  es: {
    thanksTitle: '¡Gracias por su apoyo!',
    thanksBody: 'Su gesto ayuda a mantener el sitio gratuito y viva la lengua duala: la efeméride, el diccionario, las pronunciaciones, el quiz y sus novedades. Cada contribución cuenta, sea cual sea su tamaño.',
    thanksNote: 'Si donó por Orange Money o MTN, su operador le envía un mensaje de confirmación.',
    share: 'Compartir el sitio', shareText: 'Apoyo la Efeméride Duala: un proverbio duala cada día, un diccionario ilustrado, un quiz. Descúbralo:',
    cancelTitle: 'Su donación no se completó',
    cancelBody: 'No se ha cobrado ningún importe. Quizá interrumpió el pago: no pasa nada, puede volver a intentarlo cuando quiera.',
    cancelNote: '¿Problemas para pagar? Escríbanos, con gusto le ayudaremos.',
    home: 'Volver a la efeméride', dictionary: 'Ver el diccionario',
  },
  de: {
    thanksTitle: 'Danke für Ihre Unterstützung!',
    thanksBody: 'Ihre Spende hilft, die Seite kostenlos zu halten und die Duala-Sprache lebendig zu erhalten: das Tagesblatt, das Wörterbuch, die Aussprachen, das Quiz und ihre Neuerungen. Jeder Beitrag zählt, egal wie groß.',
    thanksNote: 'Wenn Sie über Orange Money oder MTN gespendet haben, sendet Ihnen Ihr Anbieter eine Bestätigung.',
    share: 'Seite teilen', shareText: 'Ich unterstütze das Duala-Tagesblatt: jeden Tag ein Duala-Sprichwort, ein bebildertes Wörterbuch, ein Quiz. Schauen Sie vorbei:',
    cancelTitle: 'Ihre Spende wurde nicht abgeschlossen',
    cancelBody: 'Es wurde kein Betrag abgebucht. Vielleicht haben Sie die Zahlung abgebrochen: das ist nicht schlimm, Sie können es jederzeit erneut versuchen.',
    cancelNote: 'Probleme beim Bezahlen? Schreiben Sie uns, wir helfen gern.',
    home: 'Zurück zum Tagesblatt', dictionary: 'Wörterbuch ansehen',
  },
};

// Langue : ?lang=fr|en|es|de, sinon celle du navigateur, sinon le francais.
function pickLanguage(param) {
  if (TEXTS[param]) return param;
  const nav = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'fr').slice(0, 2).toLowerCase();
  return TEXTS[nav] ? nav : 'fr';
}

function DonationReturn({ kind }) {
  const [params] = useSearchParams();
  const language = pickLanguage(params.get('lang'));
  const t = TEXTS[language];
  const thanks = kind === 'thanks';

  // Pages sans interet pour les moteurs de recherche
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const shareUrl = `https://wa.me/?text=${encodeURIComponent(`${t.shareText} ${window.location.origin}/aujourdhui`)}`;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', borderRadius: 3 }}>
      <Box sx={{ color: thanks ? 'error.main' : 'text.secondary', mb: 1 }}>
        {thanks ? <HeartIcon sx={{ fontSize: 64 }} /> : <BrokenIcon sx={{ fontSize: 64 }} />}
      </Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 2 }}>{thanks ? t.thanksTitle : t.cancelTitle}</Typography>
      <Typography sx={{ mb: 1.5, maxWidth: 560, mx: 'auto' }}>{thanks ? t.thanksBody : t.cancelBody}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>{thanks ? t.thanksNote : t.cancelNote}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
        {thanks ? (
          <Button component="a" href={shareUrl} target="_blank" rel="noopener noreferrer" variant="contained" startIcon={<WhatsAppIcon />} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>{t.share}</Button>
        ) : (
          <DonateButton language={language} variant="contained" size="medium" />
        )}
        <Button component={RouterLink} to="/aujourdhui" variant="outlined" startIcon={<HomeIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>{t.home}</Button>
        <Button component={RouterLink} to="/dictionnaire" variant="text" startIcon={<BookIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>{t.dictionary}</Button>
      </Stack>
    </Paper>
  );
}

export default DonationReturn;
